package com.siriusxm.pia.views.unifiedaggregator.replay

import com.siriusxm.unifiedcontent.console.aggregator.intakeErrorMessage
import androidx.compose.runtime.*
import com.siriusxm.cmc.Notification
import com.siriusxm.cmc.NotificationTypes
import com.siriusxm.cmc.error
import com.siriusxm.pia.components.*
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.typeSelect
import unifiedcontent.aggregator.*
import kotlinx.coroutines.*
import kotlin.time.Duration.Companion.seconds
import kotlin.time.Instant

/**
 * Dialog form for starting a new replay job. Allows the user to specify
 * a target ARN, select entity types to replay, and define the event time window
 * (start and end timestamps). Submits a ReplayRequest to the aggregator API, which authorizes the
 * caller through AVP (UnifiedContentAggregator_ReplayStart); a 403 or 503 is surfaced as such.
 */
@Composable
fun startReplay(aggregator: AggregatorService) {
    var errors by remember { mutableStateOf<List<String>?>(null) }
    var targetArn by remember { mutableStateOf<String?>(null) }
    var types by remember { mutableStateOf<Collection<EntityTypeConfiguration>>(emptyList()) }
    var startTimestamp by remember { mutableStateOf<Instant?>(null) }
    var endTimestamp by remember { mutableStateOf<Instant?>(null) }

    suspend fun runReplay() {
        if (targetArn.isNullOrBlank()) {
            errors = listOf("You must enter a target ARN")
            return
        }
        if (types.isEmpty()) {
            errors = listOf("You must select at least one type")
            return
        }
        if (startTimestamp == null) {
            errors = listOf("You must specify a start timestamp for the replay window")
            return
        }
        if (endTimestamp == null) {
            errors = listOf("You must specify an end timestamp for the replay window")
            return
        }
        if (startTimestamp!! >= endTimestamp!!) {
            errors = listOf("The start timestamp must be before the end timestamp")
            return
        }

        try {
            val result = aggregator.clientApi.postReplayRequest(
                ReplayRequest(
                    targetArn = targetArn!!,
                    schemas = types.map { it.schema },
                    startTime = startTimestamp!!,
                    endTime = endTimestamp!!
                )
            )

            try {
                withTimeout(10.seconds) {
                    while (true) {
                        try {
                            aggregator.clientApi.getReplay(result.id)
                            break
                        } catch (_: Throwable) {
                            delay(1.seconds)
                        }
                    }
                    aggregator.navigate("backfill/replays/${result.id}")
                }
            } catch (_: TimeoutCancellationException) {
                aggregator.notifications.error("The replay job hasn't started yet. It may yet still, we've just timed out waiting for it.")
                aggregator.navigate("backfill/replays")
            }
        } catch (t: Throwable) {
            t.printStackTrace()
            val failure = intakeErrorMessage("start this replay", t)
            aggregator.notifications.error(failure.message, failure.description)
        }
    }

    dialogView("Start Replay") {
        content {
            dialogField("Target ARN", "The ARN of the replay target (Kinesis Stream, Event Bridge Bus, Lambda, SQS Queue)") {
                simpleTextField(targetArn, true) {
                    targetArn = it
                    errors = null
                }
            }

            dialogField("Types", "Select the types to include in the replay") {
                aggregator.typeSelect(types) {
                    types = it
                }
            }

            dialogField(
                "Start Timestamp",
                "Start of the event time window to replay (events from this time forward)."
            ) {
                instantPicker(
                    startTimestamp,
                    limit = endTimestamp?.let { OpenEndedRange(null, it) }
                ) {
                    startTimestamp = it
                }
            }

            dialogField(
                "End Timestamp",
                "End of the event time window to replay (events up to this time)."
            ) {
                instantPicker(
                    endTimestamp,
                    limit = startTimestamp?.let { OpenEndedRange(it, null) }
                ) {
                    endTimestamp = it
                }
            }

            errors?.forEach { error ->
                NotificationBox(
                    Notification(
                        style = NotificationTypes.Error, message = error, description = null
                    )
                ) {}
            }
        }

        this.action {
            title = "Cancel"
            enabled = true
            action {
                aggregator.navigate("backfill/replays")
            }
        }

        this.action {
            title = "Start"
            primary = true
            enabled = !targetArn.isNullOrBlank() && types.isNotEmpty() &&
                    startTimestamp != null && endTimestamp != null
            showProgressOnAction = true
            action {
                errors = null
                runReplay()
            }
        }
    }
}
